import React, { useMemo } from "react";
import useSEO from "@/hooks/useSEO";
import ElfLetterGenerator from "@/components/letters/ElfLetterGenerator";
import { MagicLetterBoxLink } from "@/components/funnel/MagicLetterBoxLink";
import LetterExamples from "@/components/funnel/LetterExamples";
import { getCanonicalUrl } from "@/utils/seoUtils";

const ElfLetterGeneratorPage = () => {
  const schema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Free Elf Letter Generator",
      url: getCanonicalUrl("/elf-letter-generator"),
      description:
        "Generate personalized elf letters for your kids in minutes. Receive printable PDFs by email and upgrade for unlimited magical templates.",
      applicationCategory: "EntertainmentApplication",
      operatingSystem: "Web",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        availability: "https://schema.org/OnlineOnly",
      },
      publisher: {
        "@type": "Organization",
        name: "Elf Letter Bot",
        url: "https://elfletterbot.com",
      },
      sameAs: ["https://themagicletterbox.com"],
      potentialAction: {
        "@type": "CreateAction",
        name: "Generate Elf Letters",
        target: {
          "@type": "EntryPoint",
          urlTemplate: getCanonicalUrl("/elf-letter-generator"),
        },
        result: {
          "@type": "CreativeWork",
          name: "Personalized Elf Letter PDF",
          encodingFormat: "application/pdf",
        },
      },
    }),
    [],
  );

  useSEO({
    title: "Free Elf Letter Generator | Elf Letter Bot",
    description:
      "Create personalized elf letters with 3 free PDF deliveries per email address. Explore The Magic Letter Box for unlimited letters with Family membership.",
    canonicalUrl: getCanonicalUrl("/elf-letter-generator"),
    schema,
  });

  return (
    <>
      <section className="bg-elfGreen-light py-8 md:py-16 lg:py-24">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 md:px-4 md:py-1.5 rounded-full bg-elfRed/10 text-elfRed-ink font-semibold text-xs md:text-sm uppercase tracking-wide">
            Free Elf Letter Generator
          </span>
          <h1 className="mt-4 md:mt-6 text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
            Create Magical Elf Letters in Minutes
          </h1>
          <p className="mt-3 md:mt-4 text-base md:text-lg lg:text-xl text-muted-foreground">
            Personalize a festive letter from Santa&apos;s helpers, then enter your email
            to receive the printable PDF. Includes 3 free PDF deliveries per email address.
          </p>
          <p className="mt-4 md:mt-6 text-xs md:text-sm text-muted-foreground">
            Looking for unlimited letters year-round?{" "}
            <MagicLetterBoxLink
              source="generator-hero"
              className="rounded text-elfRed-ink hover:text-[#892638] font-semibold underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700"
            >
              Explore The Magic Letter Box
            </MagicLetterBoxLink>
            .
          </p>
        </div>
      </section>

      <section className="py-8 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <ElfLetterGenerator />
          <LetterExamples source="generator" />
        </div>
      </section>
    </>
  );
};

export default ElfLetterGeneratorPage;

